import { useState } from "react";
import { toast } from "sonner";
import { Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ExportMenu } from "@/components/reports/ExportMenu";
import { SharePrepareDialog } from "@/components/reports/SharePrepareDialog";

/**
 * ShareExportPanel — Fase 4.5
 * Combina "Exportar" (dropdown CSV/PDF) + "Preparar para compartir" (modal con resumen + logging).
 *
 * Props:
 *  - reportKey: string — llave del catálogo (sales, cash_flow, etc.)
 *  - reportName: string — label humano ("Reporte de ventas")
 *  - endpoint: string — endpoint del reporte (para ExportMenu y auto-download desde el modal)
 *  - params: object — filtros aplicados actualmente (se envían al endpoint origen)
 *  - exportPermission: string — permiso del reporte origen (ej. "reports:export")
 *  - filters: object — snapshot legible para el share_summary (branch_name, date_from, date_to, etc.)
 *  - stats: object — datos que rellenan el template del summary
 *  - filename: string — filename stem
 *  - availableFormats?: ["pdf","csv"] — por defecto ambos
 *  - branchId?: string — snapshot
 *  - testIdPrefix?: string
 */
export function ShareExportPanel({
  reportKey, reportName, endpoint, params = {}, exportPermission,
  filters = {}, stats = {}, filename = "reporte",
  availableFormats = ["pdf", "csv"], branchId = null,
  testIdPrefix = "share-export",
}) {
  const { hasPermission } = useAuth();
  const [shareOpen, setShareOpen] = useState(false);

  const canGenerate = hasPermission("report_exports:generate");

  async function downloadFromDialog({ format }) {
    try {
      const { data, headers } = await api.get(endpoint, {
        params: { ...params, format },
        responseType: "blob",
      });
      const blob = new Blob([data], {
        type: format === "csv" ? "text/csv;charset=utf-8" : "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      const cd = headers?.["content-disposition"] || headers?.["Content-Disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : `${filename}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Descarga lista · ${format.toUpperCase()}`);
    } catch (err) {
      let msg = "No se pudo exportar";
      try {
        const txt = await err?.response?.data?.text?.();
        if (txt) {
          try { msg = JSON.parse(txt).detail || msg; } catch { msg = txt; }
        }
      } catch { /* keep default */ }
      toast.error(msg);
      throw err;
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testIdPrefix}>
      <ExportMenu
        endpoint={endpoint}
        params={params}
        permission={exportPermission}
        filename={filename}
        testId={`${testIdPrefix}-export`}
      />
      {canGenerate && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShareOpen(true)}
          data-testid={`${testIdPrefix}-share`}
        >
          <Share2 className="h-4 w-4 mr-2" /> Preparar para compartir
        </Button>
      )}
      <SharePrepareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        reportKey={reportKey}
        reportName={reportName}
        filters={filters}
        stats={stats}
        availableFormats={availableFormats}
        onDownload={downloadFromDialog}
        branchId={branchId}
      />
    </div>
  );
}
